import Vue from "vue";
import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";

import { DIContainer } from "@/app.container";
import { ApiFilterValue } from "@/modules/api/api-filter-value.type";
import { Dictionary } from "@/lib/Dictionary.type";
import { QueryOrderParameter } from "@/modules/api/query-order-parameter";
import dataStore from "@/store";

import UserInfo from "../user-info.model";

const name = "AccountStore";

if ((dataStore.state as any)[name]) {
  dataStore.unregisterModule(name);
}

interface UserRelations {
  userRole?: string;
}

@Module({ name, dynamic: true, store: dataStore })
export default class AccountStore extends VuexModule {
  userInfo: Dictionary<UserInfo> = {};

  // ################################### USER INFO #########################################

  get getUserInfoById(): (id: string) => UserInfo | undefined {
    return (id: string) => this.userInfo[id];
  }

  @Mutation
  removeUserInfo(id: string) {
    delete this.userInfo[id];
  }

  @Mutation
  updateUserInfo(payload: UserInfo) {
    Vue.set(this.userInfo, payload.id, payload);
  }

  @Action({ rawError: true })
  async deleteUserInfoById(id: string) {
    await DIContainer.UserInfoRepository.deleteById(id);

    this.removeUserInfo(id);

    return;
  }

  @Action({ rawError: true })
  async loadUserInfos({
    page = 1,
    limit = 20,
    filter,
    order,
  }: {
    page?: number;
    limit?: number;
    filter?: Dictionary<ApiFilterValue>;
    order?: QueryOrderParameter;
  }) {
    const collection = await DIContainer.UserInfoRepository.getList(
      page,
      limit,
      filter,
      order
    );

    collection.getItems().forEach((item) => {
      this.updateUserInfo(item);
    });

    return collection;
  }

  @Action({ rawError: true })
  async loadUserInfosByIds(ids: string[]) {
    const missing: string[] = [];
    let result: Dictionary<UserInfo | undefined> = {};

    ids.forEach((id) => {
      if (!this.userInfo[id]) {
        missing.push(id);
        return;
      }

      result[id] = this.userInfo[id];
    });

    if (!missing.length) {
      return result;
    }

    const items = await DIContainer.UserInfoRepository.getByIds(missing);

    Object.keys(items).forEach((id) => {
      const item = items[id];
      if (!item) {
        return;
      }

      this.updateUserInfo(item);
    });

    result = { ...result, ...items };

    return result;
  }

  @Action({ rawError: true })
  async loadUserInfoById({
    id,
    useCache = true,
  }: {
    id: string;
    useCache?: boolean;
  }) {
    if (useCache && this.userInfo[id]) {
      return this.userInfo[id];
    }

    const item = await DIContainer.UserInfoRepository.getById(id);

    if (!item) {
      return;
    }

    this.updateUserInfo(item);

    return item;
  }

  @Action({ rawError: true })
  async saveUserInfo(item: UserInfo) {
    item = await DIContainer.UserInfoRepository.save(item);

    this.updateUserInfo(item);

    return item;
  }
}
